<template>
    <div class="modal fade show hotel-modal" aria-labelledby="payModalTitle">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
            <div class="modal-content">
                <div class="d-flex justify-content-between align-items-center gap-1 px-1 my-1">
                    <h1 class="modal-heading mb-0">Payment</h1>
                    <div class="d-flex align-items-center gap-1 flex-shrink-0">
                        <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
                    </div>
                </div>

                <hr class="mt-0">

                <div class="modal-body">
                    <div v-if="detailLoading" class="text-center py-3">Loading reservation…</div>
                    <div class="detail-section mb-2">
                        <div class="section-title">Reservation Details</div>
                        <div class="row g-1">
                            <div class="col-xxl-3 col-md-4 col-6">
                                <div class="detail-label">Reservation No</div>
                                <div class="detail-value">{{ textValue(details.reservation_no) }}</div>
                            </div>
                            <div class="col-xxl-3 col-md-4 col-6">
                                <div class="detail-label">Status</div>
                                <div class="detail-value">
                                    <span class="badge" :class="statusClass">{{ statusLabel }}</span>
                                </div>
                            </div>
                            <div class="col-xxl-3 col-md-4 col-6">
                                <div class="detail-label">Guest</div>
                                <div class="detail-value">{{ textValue(details.guest_name) }}</div>
                            </div>
                            <div class="col-xxl-3 col-md-4 col-6">
                                <div class="detail-label">Mobile No</div>
                                <div class="detail-value">{{ textValue(details.guest_mobile) }}</div>
                            </div>
                            <div class="col-xxl-3 col-md-4 col-6">
                                <div class="detail-label">Check-in</div>
                                <div class="detail-value">{{ textValue(details.check_in_formatted) }}</div>
                            </div>
                            <div class="col-xxl-3 col-md-4 col-6">
                                <div class="detail-label">Check-out</div>
                                <div class="detail-value">{{ textValue(details.check_out_formatted) }}</div>
                            </div>
                            <div class="col-xxl-3 col-md-4 col-6">
                                <div class="detail-label">Nights</div>
                                <div class="detail-value">{{ textValue(details.nights) }}</div>
                            </div>
                            <div class="col-xxl-3 col-md-4 col-6">
                                <div class="detail-label">Payment</div>
                                <div class="detail-value">
                                    <span class="badge" :class="paymentClass">{{ paymentLabel }}</span>
                                </div>
                            </div>
                            <div v-if="details.booked_by" class="col-xxl-3 col-md-4 col-6">
                                <div class="detail-label">Booked By</div>
                                <div class="detail-value">{{ details.booked_by.name }}</div>
                            </div>
                            <div v-if="details.referrer" class="col-xxl-3 col-md-4 col-6">
                                <div class="detail-label">Referrer</div>
                                <div class="detail-value">{{ details.referrer.name }}</div>
                            </div>
                            <div v-if="details.notes" class="col-12">
                                <div class="detail-label">Notes</div>
                                <div class="detail-value">{{ details.notes }}</div>
                            </div>
                        </div>
                    </div>

                    <div v-if="roomRows.length" class="detail-section mb-2">
                        <div class="section-title">Rooms</div>
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered detail-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Room</th>
                                        <th class="text-end">Rate</th>
                                        <th class="text-end">Nights</th>
                                        <th class="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="room in roomRows" :key="room.service_resource_id">
                                        <td>{{ room.name }}</td>
                                        <td class="text-end">{{ commaFormat(room.rate) }}</td>
                                        <td class="text-end">{{ nightCount }}</td>
                                        <td class="text-end">{{ commaFormat(room.amount) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="row g-1 mb-2">
                        <div class="col-sm-4 col-12">
                            <div class="summary-card summary-total">
                                <span class="summary-label">Estimate Bill</span>
                                <span class="summary-value">{{ commaFormat(details.total) }}</span>
                            </div>
                        </div>
                        <div class="col-sm-4 col-12">
                            <div class="summary-card summary-paid">
                                <span class="summary-label">Paid</span>
                                <span class="summary-value">{{ commaFormat(details.paid) }}</span>
                            </div>
                        </div>
                        <div class="col-sm-4 col-12">
                            <div class="summary-card summary-due">
                                <span class="summary-label">Due</span>
                                <span class="summary-value">{{ commaFormat(details.due) }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="paymentRows.length" class="detail-section mb-2">
                        <div class="section-title">Payment History</div>
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered detail-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Voucher</th>
                                        <th>Received In</th>
                                        <th>Reference</th>
                                        <th class="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="payment in paymentRows" :key="payment.id">
                                        <td>{{ textValue(payment.receipt_date) }}</td>
                                        <td>{{ textValue(payment.voucher_no) }}</td>
                                        <td>{{ textValue(payment.account_head_name) }}</td>
                                        <td>{{ textValue(payment.receipt_reference) }}</td>
                                        <td class="text-end">{{ commaFormat(payment.total_paid_amount) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="section-title">Payment Info</div>

                    <form @submit.prevent="onSubmit">
                        <div class="row g-1">
                            <div class="col-xxl-3 col-md-6 mb-1">
                                <label class="form-label">Received In</label>
                                <v-select
                                    placeholder="Select Cash & Bank Account"
                                    v-model="formData.account_head_id"
                                    :options="accountHeads"
                                    :reduce="head => head.id"
                                    label="name"
                                />
                            </div>
                            <div class="col-xxl-3 col-md-6 mb-1">
                                <label class="form-label">Received Date</label>
                                <input v-model="formData.date" type="date" class="form-control"/>
                            </div>
                            <div class="col-xxl-3 col-md-6 mb-1">
                                <label class="form-label">Advance Amount</label>
                                <input v-model="formData.amount" type="number" min="0" :max="details.due" class="form-control"/>
                            </div>
                            <div class="col-xxl-3 col-md-6 mb-1">
                                <label class="form-label">Reference</label>
                                <input v-model="formData.reference" type="text" class="form-control"/>
                            </div>
                        </div>

                        <div class="d-flex flex-wrap justify-content-end gap-1 mt-2">
                            <button type="button" class="btn btn-outline-secondary flex-grow-1 flex-sm-grow-0" @click="closeModal">Cancel</button>
                            <button v-if="showDetails" type="button" class="btn btn-info flex-grow-1 flex-sm-grow-0" @click="$emit('onSeeDetails', reservation)">Detail Billing</button>
                            <button type="submit" :disabled="loading" class="btn btn-primary flex-grow-1 flex-sm-grow-0">Pay</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import handleHotel from '@/services/modules/hotel/hotel';
import handleReceipt from '@/services/modules/receipt';
import figureFormatter from '@/services/utils/figureFormatter';

const props = defineProps({
    reservation: {
        type: Object,
        required: true,
    },
    showDetails: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['onSubmit', 'onClose', 'onSeeDetails']);

const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { payHotelReservation, fetchSingleHotelReservation } = handleHotel();
const { fetchCashAndBank } = handleReceipt();
const { commaFormat } = figureFormatter();

const loading = ref(false);
const detailLoading = ref(false);
const details = ref({});
const accountHeads = ref([]);
const formData = ref({
    company_id: $route.params.companyId,
    account_head_id: null,
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    reference: '',
});

const statusBadges = {
    pending: 'bg-light-warning',
    confirmed: 'bg-light-info',
    checked_in: 'bg-light-success',
    released: 'bg-light-secondary',
    completed: 'bg-light-secondary',
    cancelled: 'bg-light-danger',
};

const paymentBadges = {
    paid: 'bg-light-success',
    partial: 'bg-light-warning',
    unpaid: 'bg-light-danger',
};

const humanize = (value) => {
    if (!value) return '-';
    return value.split('_').map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
};

const textValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
};

const statusLabel = computed(() => humanize(details.value.registration_status));

const paymentLabel = computed(() => humanize(details.value.payment_status));

const statusClass = computed(() => {
    const badge = statusBadges[details.value.registration_status];
    if (!badge) return 'bg-light-secondary';
    return badge;
});

const paymentClass = computed(() => {
    const badge = paymentBadges[details.value.payment_status];
    if (!badge) return 'bg-light-secondary';
    return badge;
});

const nightCount = computed(() => {
    if (!details.value.nights) return 1;
    return Number(details.value.nights);
});

const roomRows = computed(() => {
    if (!details.value.rooms) return [];
    return details.value.rooms.map((room) => {
        return Object.assign({}, room, { amount: Number(room.rate) * nightCount.value });
    });
});

const paymentRows = computed(() => {
    if (!details.value.payments) return [];
    return details.value.payments;
});

const closeModal = () => {
    emit('onClose');
};

const validate = () => {
    if (!formData.value.account_head_id) {
        showError('Please select the received account');
        return false;
    }
    if (!formData.value.amount || formData.value.amount <= 0) {
        showError('Please enter a valid amount');
        return false;
    }
    if (Number(formData.value.amount) > Number(details.value.due)) {
        showError('Amount cannot exceed due');
        return false;
    }
    return true;
};

const getReservationDetails = async () => {
    detailLoading.value = true;
    const query = `?company_id=${$route.params.companyId}`;
    await fetchSingleHotelReservation(query, props.reservation.id).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        details.value = res.data;
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        detailLoading.value = false;
    });
};

const onSubmit = async () => {
    if (!validate()) return;
    loading.value = true;
    await payHotelReservation(formData.value, props.reservation.id).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        emit('onSubmit');
        closeModal();
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

onMounted(() => {
    getReservationDetails();
    fetchCashAndBank($route.params.companyId).then(res => {
        if (res.data) accountHeads.value = res.data;
    });
});
</script>

<style scoped>
.hotel-modal {
    display: block;
    background: #1c1d1c33;
}
.section-title {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #5e5873;
    margin-bottom: 0.5rem;
}
.detail-section {
    background: #f8f8f8;
    border: 1px solid #ebe9f1;
    border-radius: 0.5rem;
    padding: 0.75rem;
}
.detail-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6e6b7b;
    opacity: 0.85;
}
.detail-value {
    font-size: 0.88rem;
    font-weight: 600;
    color: #384250;
    word-break: break-word;
}
.summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0.65rem 0.5rem;
    border-radius: 0.5rem;
    border: 1px solid transparent;
    height: 100%;
}
.summary-label {
    font-size: 0.72rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    opacity: 0.75;
    margin-bottom: 0.15rem;
}
.summary-value {
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.2;
}
.summary-total {
    background: #eef2f7;
    border-color: #dfe6ef;
    color: #384250;
}
.summary-paid {
    background: #e7f6ec;
    border-color: #cdeed8;
    color: #1a7f43;
}
.summary-due {
    background: #fdeceb;
    border-color: #f8d5d2;
    color: #c0392b;
}
.modal-heading {
    font-size: 1.5rem;
}
.detail-table {
    min-width: 480px;
}
@media (max-width: 767.98px) {
    .modal-heading {
        font-size: 1.15rem;
    }
    .detail-section {
        padding: 0.6rem;
    }
    .summary-card {
        flex-direction: row;
        justify-content: space-between;
        padding: 0.5rem 0.75rem;
    }
    .summary-label {
        margin-bottom: 0;
    }
    .summary-value {
        font-size: 0.95rem;
    }
}
</style>
